import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  initAuth,
  onAuthChange,
  logout as apiLogout,
  loginWithMagicCode as apiLoginWithMagicCode,
  devLogin as apiDevLogin,
  isSuperuser as checkIsSuperuser,
  canCreateGames as checkCanCreateGames,
} from "../api/client";

interface User {
  id: string;
  discordId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  role?: "superuser";
  canCreateGames?: boolean;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isSuperuser: boolean;
  canCreateGames: boolean;
  logout: () => void;
  loginWithMagicCode: (
    code: string,
  ) => Promise<{ success: boolean; error?: string }>;
  devLogin: (
    discordId: string,
    username: string,
    options?: { role?: "superuser"; canCreateGames?: boolean },
  ) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize auth on mount - synchronous, decodes JWT immediately
    initAuth();
    setLoading(false);

    // Subscribe to auth changes (background refresh may update user)
    const unsubscribe = onAuthChange((user) => {
      setUser(user);
    });

    return unsubscribe;
  }, []);

  const logout = () => {
    apiLogout();
    // Redirect to home after logout
    window.location.href = "/spa";
  };

  const loginWithMagicCode = async (code: string) => {
    const result = await apiLoginWithMagicCode(code);
    if (result.success) {
      // Redirect after successful login
      const postLoginUrl = window.localStorage.getItem("postLoginUrl");
      window.location.href = postLoginUrl ?? "/spa/welcome";
      window.localStorage.removeItem("postLoginUrl");
    }
    return result;
  };

  const devLogin = async (
    discordId: string,
    username: string,
    options?: { role?: "superuser"; canCreateGames?: boolean },
  ) => {
    const result = await apiDevLogin(discordId, username, options);
    if (result.success) {
      const postLoginUrl = window.localStorage.getItem("postLoginUrl");
      window.location.href = postLoginUrl ?? "/spa/welcome";
      window.localStorage.removeItem("postLoginUrl");
    }
    return result;
  };

  const value: AuthContextValue = {
    user,
    loading,
    isAuthenticated: user !== null,
    isSuperuser: checkIsSuperuser(),
    canCreateGames: checkCanCreateGames(),
    logout,
    loginWithMagicCode,
    devLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
